/**
 * @prettier
 */

// React Packages
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withTranslation } from "react-i18next";

// Other Packages
import i18next from "i18next";
import axios from "axios";

// Actions
import {
    decrementSidesLeft,
    incrementSidesLeft,
    resetIDProperties,
    setCardOrientation,
    setCardType,
    isExpiredTwoPagePassport,
} from "../../../state/actions/idPropertiesActions";
import {
    setRetryLimit,
    completeStage,
    submissionAttempt,
} from "../../../state/actions/submissionStateActions";
import { clearError } from "../../../state/actions/navigatorActions";

// Services
import documentService from "../../../services/shared/documentService";
import {
    applyCropSettings,
    isTwoPagePassport,
    getSubmissionType,
    getDocumentTitle,
    applyRetryDocumentUpload,
} from "../../../services/shared/processedDocumentService";
import apiService from "../../../services/api/api";
import logService from "../../../services/shared/logService";
import selectStorage from "../../../services/shared/selectStorage";
import {
    areCookiesEnabled,
    isSubmissionComplete,
    getKeyByValue,
} from "../../../services/shared/helpers";

// Config
import DataDogService from "../../../services/shared/datadogService";
import { allowedDocumentsTypes, titleMap } from "../../../config/documents";

// Components
import Navigator from "../../../components/Navigator";
import Header from "../../../components/Header";
import DocumentReviewState from "../components/DocumentReviewState";
import FailedVerification from "../components/FailedVerificationState";
import OutOfRetryAttemptsState from "../components/OutOfRetryAttemptsState";
import SubmissionCompleteState from "../components/SubmissionCompleteState";
import DocumentUploadCompletedState from "../components/DocumentUploadCompleteState";
import SubmissionFlowStepper from "../../../components/SubmissionFlowStepper";
import ValidationMessageModals from "../components/ValidationMessageModals";
import ProcessingLayout from "../../../components/ProcessingLayout";

class RegulaResultPage extends Component {
    constructor(props) {
        super(props);
        this.pageFocusRef = React.createRef();
        this.state = {
            currentDocumentRegion: 0,
            document: {},
            processing: false,
            validation: {
                messages: "",
                validated: true,
            },
            classification: {
                messages: "",
                classified: false,
                detail: "",
            },
            callType: "classify",
            failed: false,
            expired: false,
            isAcceptedDoc: true,
            navigation: {
                action: "load",
                props: null,
            },
        };
        this.retryDocumentUpload = this.retryDocumentUpload.bind(this);
        this.proceedToNextStep = this.proceedToNextStep.bind(this);

        // Cancels the classification request
        this.CancelToken = axios.CancelToken;
        this.source = this.CancelToken.source();
    }

    /**
     * React component life cycle hook.
     */
    componentDidMount() {
        // Set the callType to display the correct loading messages
        if (this.props.sidesLeft === 2) {
            this.setState({ callType: "classify" });
        } else {
            this.setState({ callType: "verify" });
        }

        // Set document title based on submission state
        const { t } = this.props;
        if (this.props.completed === 1) {
            document.title = t("idpal_doc_title_message_complete");
        }

        if (this.props.completed === 1) {
            return;
        }

        // Sets focus to primary heading on first render
        if (this.pageFocusRef && this.pageFocusRef.current) {
            this.pageFocusRef.current.focus();
        }

        // Record a submission attempt on this side
        this.props.submissionAttempt(
            this.props.sidesLeft === 2 ? "front" : "back"
        );

        // Set state properties
        this.setState({
            processing: false,
            document: documentService.findById(
                this.props.location.state.documentId
            ),
        });

        // Back of ID does not need to be classified
        if (this.props.orientation === 1) {
            this.setState({
                classification: {
                    ...this.state.classification,
                    classified: true,
                },
                processing: false,
            });
        } else {
            this.classifyUploadedDocument(
                documentService.findById(this.props.location.state.documentId),
                this.source.token
            );
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { t } = this.props;

        // Set document title based on submission state
        document.title = t(getDocumentTitle(this.state, this.props.completed));

        if (
            this.state.classification.messages !==
            prevState.classification.messages
        ) {
            DataDogService.log(
                `Classification Message: ${this.state.classification.messages}`
            );
        }

        if (
            this.state.classification.detail !== prevState.classification.detail
        ) {
            DataDogService.log(
                `Classification Call Status: ${this.state.classification.detail}`
            );
        }
    }

    componentWillUnmount() {
        // Cancel request on component dismount
        this.source.cancel("Cancelled");
    }

    /**
     * Classify uploaded document.
     *
     * @param selectedDocument
     */
    classifyUploadedDocument(selectedDocument, cancelToken) {
        this.showSpinner();
        // Clear any error messages
        clearError(this);

        // Get document classification using cropped or uncropped image, based on setting. If no cropped is available, fallback to uncropped
        apiService
            .getDocumentClassification(
                documentService.urlToBlob(
                    this.props.croppedImage &&
                        this.props.classificationCrop === 1
                        ? this.props.croppedImage
                        : this.props.uncroppedImage
                ),
                cancelToken
            )
            .then(response => {
                let classifiedDocument = null;

                if (response.data) {
                    // Set document based on classification
                    classifiedDocument = documentService.findByClassification(
                        response.data.classification
                    );

                    // Set classification response to log to datadog
                    this.setState({
                        classification: {
                            ...this.state.classification,
                            detail: response.detail,
                        },
                    });

                    if (classifiedDocument) {
                        // Update card type to the classified card type
                        this.props.setCardType(classifiedDocument.id);

                        // Check if it is an allowed cardType
                        if (this.props.isDocSelectionEnabled) {
                            this.setState({
                                isAcceptedDoc: this.isAcceptedDoc(
                                    classifiedDocument.name,
                                    allowedDocumentsTypes
                                ),
                            });
                        }
                    }
                    // Log outcome
                    logService.log("Document classified.", classifiedDocument);
                    this.hideSpinner();
                } else {
                    // Could not get classification, fall back to selected document
                    classifiedDocument = selectedDocument;

                    logService.log(
                        "Document not classified, fallback to manually selected",
                        document
                    );
                }

                // Only set classification state if we have document or if doc is a paper licence
                if (
                    classifiedDocument ||
                    selectedDocument.name === "Paper Licence"
                ) {
                    if (classifiedDocument) {
                        this.setState({
                            currentDocumentRegion: response.data
                                ? response.data.classification.documentRegion
                                : this.state.currentDocumentRegion,
                        });
                    }

                    this.setState({
                        classification: {
                            ...this.state.classification,
                            classified: true,
                        },
                        document: classifiedDocument
                            ? classifiedDocument
                            : selectedDocument,
                    });
                } else {
                    // set error message if document is not classifiable
                    this.setState({
                        classification: {
                            ...this.state.classification,
                            messages: i18next.t("idpal_classification_error"),
                        },
                    });
                }

                if (this.state.classification.classified) {
                    // Set the callType to verify to display the loading messages
                    this.setState({
                        callType: "verify",
                        processing: false,
                    });
                } else {
                    this.setState({
                        processing: false,
                    });
                }
            })
            .catch(error => {
                if (error === "Cancelled") {
                    logService.error(error);
                } else {
                    DataDogService.createError(
                        "Could not retrieve document classification."
                    );
                    this.setState({
                        navigation: {
                            action: "error",
                            props: {
                                retryAction: () =>
                                    this.classifyUploadedDocument(
                                        selectedDocument,
                                        cancelToken
                                    ),
                                error: error,
                            },
                        },
                    });
                }
                this.hideSpinner();
            });
    }

    processVerification(response) {
        this.showSpinner();

        // Update the sessionId value in selectStorage(areCookiesEnabled()) if the one is returned from the response
        if (response.data && response.data.hasOwnProperty("sessionid")) {
            selectStorage(areCookiesEnabled()).setItem(
                "sessionid",
                response.data.sessionid
            );
        }

        // Handle documents which require two pages to be submitted
        if (this.state.document.pages.single === false) {
            // Set relevant states
            this.props.decrementSidesLeft();
            this.props.setCardOrientation(1);

            // Capture second document
            if (this.props.sidesLeft === 1) {
                selectStorage(areCookiesEnabled()).setItem(
                    "documentRegion",
                    this.state.currentDocumentRegion
                );
                this.setState({
                    navigation: {
                        action: "next",
                        props: {
                            documentId: this.state.document.id,
                            sidesLeft: this.props.sidesLeft,
                        },
                    },
                });
            } else {
                // Handle UI transitions
                if (
                    response.code === 200 &&
                    response.data.hasOwnProperty("verification")
                ) {
                    if (
                        response.data.verification.status !== "Passed" &&
                        this.props.submissionAttempts.remaining > 0
                    ) {
                        this.setState({
                            expired: response.data.verification.expired,
                        });
                        this.setState({ failed: true });
                        DataDogService.log(
                            "Verification Call Status: Failed to verify"
                        );
                        this.hideSpinner();
                    } else {
                        this.documentCheckComplete();
                    }

                    // Clear document region once back image has been uploaded
                    selectStorage(areCookiesEnabled()).removeItem(
                        "documentRegion"
                    );
                } else if (response.code === 202) {
                    // Successful response, but no verification data, accept document and proceed.
                    this.documentCheckComplete();
                } else {
                    this.setState({
                        navigation: {
                            action: "error",
                        },
                    });
                }
            }
        }

        // Handle documents which require only page to be submitted
        if (
            this.state.document.pages.single === true &&
            this.state.document.pages.number === 1
        ) {
            // Set state
            this.props.decrementSidesLeft();

            // Handle UI transitions
            if (
                response.code === 200 &&
                response.data.hasOwnProperty("verification")
            ) {
                // Failed verification, recapture if attempts remaining
                if (
                    response.data.verification.status !== "Passed" &&
                    this.props.submissionAttempts.remaining > 0
                ) {
                    this.setState({
                        expired: response.data.verification.expired,
                        failed: true,
                    });

                    DataDogService.log(
                        "Verification Call Status: Failed to verify"
                    );
                    this.hideSpinner();
                } else {
                    this.documentCheckComplete();
                }
            } else if (
                response.code === 200 &&
                this.state.document.name === "Paper Licence"
            ) {
                // No verification for Paper Licence
                this.documentCheckComplete();
            } else if (response.code === 202) {
                // Successful response, but no verification data, accept document and proceed.
                this.documentCheckComplete();
            } else {
                this.setState({
                    navigation: {
                        action: "error",
                    },
                });
            }
        }
    }

    /**
     * Upload document to the API
     *
     * @param blobData
     * @param cropped
     */
    uploadDocument(blobData, cropped, cancelToken) {
        this.showSpinner();
        clearError(this);

        const documentRegion =
            selectStorage(areCookiesEnabled()).getItem("documentRegion");

        apiService[getSubmissionType(this.state.document)](
            this.props.instanceID,
            this.props.orientation,
            blobData,
            cropped,
            documentRegion,
            cancelToken
        )
            .then(response => {
                this.processVerification(response);
            })
            .catch(error => {
                // catch if user cancelled the request
                if (error === "Cancelled") {
                    logService.error(error);
                } else {
                    DataDogService.createError(
                        "Unable to submit document for validation."
                    );
                    this.hideSpinner();
                    this.setState({
                        navigation: {
                            action: "error",
                            props: {
                                retryAction: () =>
                                    this.uploadDocument(
                                        blobData,
                                        cropped,
                                        cancelToken
                                    ),
                                error: error,
                            },
                        },
                    });
                }
            });
    }

    /**
     * Proceed to next step of user flow.
     */
    proceedToNextStep() {
        const cropped = applyCropSettings(
            this.props.cropSettings,
            this.state.document.name
        );

        const image = documentService.urlToBlob(
            cropped && this.props.croppedImage
                ? this.props.croppedImage
                : this.props.uncroppedImage
        );
        this.uploadDocument(image, cropped, this.source.token);
    }

    /**
     * Retry document upload.
     */
    retryDocumentUpload(thisSideOnly = false) {
        // Reset document validation items
        this.setState({
            classification: {
                messages: "",
                classified: false,
            },
            expired: false,
            failed: false,
        });

        this.setState({
            navigation: applyRetryDocumentUpload(
                this.state,
                this.props,
                thisSideOnly
            ),
        });
    }

    /**
     * Document check complete, continue to success or passive liveness
     */
    documentCheckComplete() {
        this.props.setRetryLimit(0);
        this.props.completeStage("document_upload");
        this.setState({
            navigation: {
                action: "next",
            },
        });
    }

    /**
     * Show spinner.
     */
    showSpinner() {
        this.setState({ processing: true });
    }

    /**
     * Hide spinner.
     */
    hideSpinner() {
        this.setState({ processing: false });
    }

    isAcceptedDoc(docType, allowedDocumentsTypes) {
        const docTypeKey = getKeyByValue(allowedDocumentsTypes, docType);

        return this.props.acceptedDocuments.includes(docTypeKey);
    }

    getListOfAcceptedDocumentTitles() {
        const { t } = this.props;

        const acceptedDocuments = this.props.acceptedDocuments.map(
            title => t(titleMap[title]) || title
        );
        return acceptedDocuments.join(", ");
    }

    /**
     * Render component.
     */
    render() {
        // Set props
        const data = this.props.location.state;
        const cardImage = isTwoPagePassport(
            this.props.onePagePassport,
            this.state.document.name
        )
            ? this.props.uncroppedImage
            : this.props.croppedImage ?? this.props.uncroppedImage;

        if (isSubmissionComplete()) {
            return (
                <SubmissionCompleteState companyName={this.props.companyName} />
            );
        }

        // Document upload is already completed
        if (this.props.completed && this.state.navigation.action === "load") {
            return (
                <DocumentUploadCompletedState
                    onContinue={() =>
                        this.setState({ navigation: { action: "next" } })
                    }
                    navigationAction={this.state.navigation.action}
                    navigationProps={this.state.navigation.props}
                />
            );
        }

        // Render loader component (while state is processing)
        if (this.state.processing) {
            return (
                <Fragment>
                    <ProcessingLayout
                        documentData={data}
                        callType={this.state.callType}
                        showMessages={true}
                    />
                    <Navigator
                        page={"document_review"}
                        action={this.state.navigation.action}
                        propsToPass={this.state.navigation.props}
                    />
                </Fragment>
            );
        }

        // Render component
        return (
            <div className="root-layout-container processed-image-result-page">
                {/* User Image capture error modals */}
                <ValidationMessageModals
                    classification={this.state.classification}
                    isAcceptedDoc={this.state.isAcceptedDoc}
                    validation={this.state.validation}
                    expired={this.state.expired}
                    failed={this.state.failed}
                    submissionAttempts={this.props.submissionAttempts}
                    allowedDocumentsTypes={this.getListOfAcceptedDocumentTitles()}
                />

                <Header />
                <SubmissionFlowStepper />
                {/*Normal Submission Process*/}
                {this.props.submissionAttempts.remaining > 0 &&
                    !this.state.failed && (
                        <DocumentReviewState
                            data={data}
                            classification={this.state.classification}
                            pageFocusRef={this.pageFocusRef}
                            validation={this.state.validation}
                            cardImage={cardImage}
                            isAcceptedDoc={this.state.isAcceptedDoc}
                            proceedToNextStep={this.proceedToNextStep}
                            retryDocumentUpload={this.retryDocumentUpload}
                        />
                    )}

                {/*Submitted ID Failed Verification*/}
                {this.state.failed && (
                    <FailedVerification
                        isExpired={this.state.expired}
                        pageFocusRef={this.pageFocusRef}
                        cardImage={cardImage}
                        retryDocumentUpload={this.retryDocumentUpload}
                    />
                )}

                {/* Out of retry attempts. Submit whatever regardless of validation or classification */}
                {this.props.submissionAttempts.remaining === 0 && (
                    <OutOfRetryAttemptsState
                        pageFocusRef={this.pageFocusRef}
                        cardImage={cardImage}
                        proceedToNextStep={this.proceedToNextStep}
                    />
                )}

                <Navigator
                    page={"document_review"}
                    action={this.state.navigation.action}
                    propsToPass={this.state.navigation.props}
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        instanceID: state.config.instanceID,
        orientation: state.idProperties.orientation,
        cardType: state.idProperties.cardType,
        sidesLeft: state.idProperties.sidesLeft,
        submissionAttempts: state.submissionState.submissionAttempts,
        completed:
            state.submissionState.submissionState.document_upload.completed,
        cropSettings: state.config.cropSettings,
        uncroppedImage: state.idProperties.uncroppedImage,
        croppedImage: state.idProperties.croppedImage,
        companyName: state.config.profile.data.company_branding.name,
        onePagePassport:
            state.submissionState.screens.document_upload.one_page_passport,
        classificationCrop:
            state.submissionState.screens.document_upload.classification_crop,
        isDocSelectionEnabled: state.submissionState.isDocSelectionEnabled,
        acceptedDocuments: state.config.profile.data.accepted_documents,
        userSelectedCardType: state.idProperties.userSelectedCardType,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            decrementSidesLeft,
            incrementSidesLeft,
            resetIDProperties,
            setCardOrientation,
            setRetryLimit,
            setCardType,
            completeStage,
            submissionAttempt,
            isExpiredTwoPagePassport,
        },
        dispatch
    );
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(RegulaResultPage)
);
